import { useCallback, useId } from "react";
import { useTranslation } from "react-i18next";
import { listDecidableResults } from "../../../api/knowledgeData/knowledgeDataApi";
import type { ExtractionSummary } from "../../../api/knowledgeData/knowledgeDataDecoders";
import { formatDateTime } from "../../../shared/formatting/dateTimes";
import { useLoad } from "../../../shared/loading/useLoad";
import { parseContentAddress } from "../../../shared/references/contentReferences";
import Chip from "../../../ui/feedback/Chip";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import OptionLabel from "../../../ui/forms/OptionLabel";
import { useDocumentName } from "../../sources/document/useSourceDocument";

interface ResultPickerProps {
  projectId: string;
  /** The input's label, naming the group. */
  label: string;
  /** The picked result's key, or none. */
  value: unknown;
  /** The step needs one: while none is picked, the picker says so, as the input's own editor. */
  required: boolean;
  onPick: (key: string) => void;
}

/**
 * The pick view of a recorded result (`dmwp:RecordedResult`, ADR-0032 D8; A8): the results
 * this person may start a decision on now, by module D's list and the start's own rule,
 * newest first, each by its document, its size and when it was recorded. The value is the
 * result's exact key, a carried revision included; nothing is typed in. With none to pick it
 * says only that; with some and none picked, a required input asks for one.
 */
export default function ResultPicker({ projectId, label, value, required, onPick }: ResultPickerProps) {
  const { t } = useTranslation();
  const group = useId();
  const load = useCallback((signal: AbortSignal) => listDecidableResults(projectId, signal), [projectId]);
  const { state, reload } = useLoad(load);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("threads.current.inputs.results.loading")} onRetry={reload} />;
  if (state.value.length === 0) return <p className="app-text-muted text-xs">{t("threads.current.inputs.results.none")}</p>;
  const picked = state.value.some((result) => result.source === value);
  return (
    <>
      <ul role="radiogroup" aria-label={label} className="max-h-48 space-y-1 overflow-y-auto">
        {state.value.map((result) => (
          <li key={result.source}>
            <OptionLabel align="start">
              <input type="radio" name={group} checked={value === result.source} onChange={() => { onPick(result.source); }} />
              <ResultText result={result} />
            </OptionLabel>
          </li>
        ))}
      </ul>
      {required && !picked ? <Chip tone="warning" size="compact">{t("threads.current.inputs.results.choose")}</Chip> : null}
    </>
  );
}

function ResultText({ result }: { result: ExtractionSummary }) {
  const { t, i18n } = useTranslation();
  const address = parseContentAddress(result.document);
  const version = address?.document.version ?? null;
  const name = useDocumentName(address === null || version === null ? null : { documentId: address.document.documentId, version });
  const size = t(result.kind === "annotations" ? "data.index.annotations" : "data.index.statements", { count: result.statements });
  return (
    <span>
      {name ?? "…"}
      <span className="app-text-muted block text-xs">{size} · {formatDateTime(result.recordedAt, i18n.resolvedLanguage)}</span>
    </span>
  );
}
